/**
 * Georeferencing math — affine transform from ground control points (GCPs),
 * residual errors, world file + KML/KMZ export. (Sprint 7.1, G44)
 *
 * A GCP pairs an image pixel (px, py — origin top-left, py down) with a
 * map location (lon, lat).
 *
 * Affine model (6 parameters), same form as an ESRI world file:
 *   lon = a·px + b·py + c
 *   lat = d·px + e·py + f
 */

/** Solve a small linear system A·x = b via Gaussian elimination with partial pivoting. */
function solveLinear(A, b) {
  const n = A.length;
  // Augmented matrix
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    // Pivot
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    }
    if (Math.abs(M[pivot][col]) < 1e-12) return null; // singular
    [M[col], M[pivot]] = [M[pivot], M[col]];
    // Eliminate
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const factor = M[r][col] / M[col][col];
      for (let c = col; c <= n; c++) M[r][c] -= factor * M[col][c];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}

/**
 * Least-squares affine transform from ≥3 GCPs.
 * @param {Array<{px:number, py:number, lon:number, lat:number}>} gcps
 * @returns {{a,b,c,d,e,f}|null}
 */
export function computeAffineTransform(gcps) {
  if (!gcps || gcps.length < 3) return null;

  // Normal equations for x' = a·px + b·py + c (and same design matrix for y')
  // Design matrix rows: [px, py, 1]
  let Sxx = 0, Sxy = 0, Sx1 = 0, Syy = 0, Sy1 = 0, S11 = gcps.length;
  let SxLon = 0, SyLon = 0, S1Lon = 0, SxLat = 0, SyLat = 0, S1Lat = 0;

  gcps.forEach(({ px, py, lon, lat }) => {
    Sxx += px * px; Sxy += px * py; Sx1 += px;
    Syy += py * py; Sy1 += py;
    SxLon += px * lon; SyLon += py * lon; S1Lon += lon;
    SxLat += px * lat; SyLat += py * lat; S1Lat += lat;
  });

  const N = [
    [Sxx, Sxy, Sx1],
    [Sxy, Syy, Sy1],
    [Sx1, Sy1, S11],
  ];

  const lonSol = solveLinear(N, [SxLon, SyLon, S1Lon]);
  const latSol = solveLinear(N, [SxLat, SyLat, S1Lat]);
  if (!lonSol || !latSol) return null;

  return { a: lonSol[0], b: lonSol[1], c: lonSol[2], d: latSol[0], e: latSol[1], f: latSol[2] };
}

/** Apply the affine transform to a pixel coordinate → [lon, lat]. */
export function applyTransform(t, px, py) {
  return [t.a * px + t.b * py + t.c, t.d * px + t.e * py + t.f];
}

/**
 * Per-GCP residual errors in meters (approx, local tangent plane) + total RMS.
 * @returns {{ residuals: number[], rms: number }}
 */
export function computeResiduals(t, gcps) {
  if (!t || !gcps?.length) return { residuals: [], rms: 0 };
  const residuals = gcps.map(({ px, py, lon, lat }) => {
    const [plon, plat] = applyTransform(t, px, py);
    const mLat = 110540;
    const mLon = 111320 * Math.cos((lat * Math.PI) / 180);
    const dx = (plon - lon) * mLon;
    const dy = (plat - lat) * mLat;
    return Math.sqrt(dx * dx + dy * dy);
  });
  const rms = Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / residuals.length);
  return { residuals, rms };
}

/**
 * Image corner coordinates for a MapLibre `image` source.
 * @returns [[TL],[TR],[BR],[BL]] as [lon,lat] pairs
 */
export function imageCornerCoordinates(t, width, height) {
  return [
    applyTransform(t, 0, 0),
    applyTransform(t, width, 0),
    applyTransform(t, width, height),
    applyTransform(t, 0, height),
  ];
}

/**
 * ESRI world file content (6 lines). Uses the pixel-center convention:
 * C/F reference the center of the top-left pixel.
 */
export function worldFileContent(t) {
  const C = t.a * 0.5 + t.b * 0.5 + t.c;
  const F = t.d * 0.5 + t.e * 0.5 + t.f;
  return [t.a, t.d, t.b, t.e, C, F].map(v => v.toPrecision(12)).join('\n') + '\n';
}

/** World file extension for a given image filename (.pgw/.jgw/.tfw/.wld). */
export function worldFileExtension(filename) {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  return { png: 'pgw', jpg: 'jgw', jpeg: 'jgw', tif: 'tfw', tiff: 'tfw', gif: 'gfw' }[ext] || 'wld';
}

/**
 * KML document with a gx:LatLonQuad GroundOverlay (supports rotated/skewed quads).
 * @param {string} name - overlay name
 * @param {string} imageHref - image path inside the KMZ (e.g. "files/image.png")
 * @param {Array} corners - [[TL],[TR],[BR],[BL]] lon/lat
 */
export function buildOverlayKML(name, imageHref, corners) {
  const [tl, tr, br, bl] = corners;
  // gx:LatLonQuad order: BL, BR, TR, TL
  const quad = [bl, br, tr, tl].map(c => `${c[0]},${c[1]}`).join(' ');
  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2" xmlns:gx="http://www.google.com/kml/ext/2.2">
  <Document>
    <name>${escapeXml(name)}</name>
    <GroundOverlay>
      <name>${escapeXml(name)}</name>
      <Icon><href>${escapeXml(imageHref)}</href></Icon>
      <gx:LatLonQuad>
        <coordinates>${quad}</coordinates>
      </gx:LatLonQuad>
    </GroundOverlay>
  </Document>
</kml>`;
}

function escapeXml(s) {
  return String(s).replace(/[<>&'"]/g, ch => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' }[ch]));
}

/**
 * Build and download a KMZ (zip with doc.kml + the image).
 * @param {string} name - overlay/document name
 * @param {File|Blob} imageBlob - the source image
 * @param {string} imageFilename - original filename (for extension)
 * @param {Array} corners - [[TL],[TR],[BR],[BL]]
 */
export async function exportKMZ(name, imageBlob, imageFilename, corners) {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const ext = (imageFilename.split('.').pop() || 'png').toLowerCase();
  const href = `files/overlay.${ext}`;
  zip.file('doc.kml', buildOverlayKML(name, href, corners));
  zip.file(href, imageBlob);
  const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.google-earth.kmz' });
  downloadBlob(blob, `${name.replace(/[^\w-]+/g, '_')}.kmz`);
}

/** Download a world file for the current transform. */
export function exportWorldFile(t, imageFilename) {
  const ext = worldFileExtension(imageFilename);
  const base = imageFilename.replace(/\.[^.]+$/, '');
  const blob = new Blob([worldFileContent(t)], { type: 'text/plain' });
  downloadBlob(blob, `${base}.${ext}`);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
